/**
 * DesignLabPage — /design-lab
 *
 * Internal design-comparison surface used BEFORE Claude Designer sessions to
 * decide accent color and typeface. Not intended for public consumption.
 * Remove or gate once decisions are made.
 *
 * Layout:
 *   Section 1: 3-column accent-color comparison (Coral · Deep Indigo · Muted Amber)
 *              Each column renders the same hero + trust bar + CTA + framework card
 *              using the candidate accent color.
 *   Section 2: 2-column typeface comparison (Geist · Inter)
 *              Each column renders the same hero content in the candidate typeface.
 */

import { Box, Button, Chip, Container, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LightbulbIcon from "@mui/icons-material/Lightbulb";

// ---------------------------------------------------------------------------
// Accent color candidates
// ---------------------------------------------------------------------------

interface Accent {
  key: string;
  labelKey: string;
  hex: string;
  noteKey: string;
}

// NOTE: hex values in ACCENTS are intentional design-comparison data, not styling tokens.
// This is an internal tool that renders candidate accent colors for visual comparison.
// The hex values ARE the subject of the comparison and must stay literal.
// label/note are i18n keys (resolved via t() at render — see pages-core.json DesignLabPage).
const ACCENTS: Accent[] = [
  {
    key: "coral",
    labelKey: "DesignLabPage.accentCoralLabel",
    hex: "#e35d6a", // candidate: maps to theme.palette.primary.main
    noteKey: "DesignLabPage.accentCoralNote",
  },
  {
    key: "indigo",
    labelKey: "DesignLabPage.accentIndigoLabel",
    hex: "#4F46E5", // candidate: no standard token equivalent (purple)
    noteKey: "DesignLabPage.accentIndigoNote",
  },
  {
    key: "amber",
    labelKey: "DesignLabPage.accentAmberLabel",
    hex: "#B45309", // candidate: maps to theme.palette.warning.main
    noteKey: "DesignLabPage.accentAmberNote",
  },
];

// ---------------------------------------------------------------------------
// Typeface candidates
// ---------------------------------------------------------------------------

interface Typeface {
  key: string;
  labelKey: string;
  family: string;
  noteKey: string;
}

const TYPEFACES: Typeface[] = [
  {
    key: "geist",
    labelKey: "DesignLabPage.typefaceGeistLabel",
    family: "'Geist', 'Inter', system-ui, sans-serif",
    noteKey: "DesignLabPage.typefaceGeistNote",
  },
  {
    key: "inter",
    labelKey: "DesignLabPage.typefaceInterLabel",
    family: "'Inter', system-ui, sans-serif",
    noteKey: "DesignLabPage.typefaceInterNote",
  },
];

// ---------------------------------------------------------------------------
// Sample content — same across every swatch for fair comparison
// (values are i18n keys, resolved via t() at render)
// ---------------------------------------------------------------------------

const SAMPLE = {
  eyebrowKey: "DesignLabPage.sampleEyebrow",
  headlineKey: "DesignLabPage.sampleHeadline",
  subheadKey: "DesignLabPage.sampleSubhead",
  ctaKey: "DesignLabPage.sampleCta",
  secondaryKey: "DesignLabPage.sampleSecondary",
  trustSignalKeys: [
    "DesignLabPage.trustSignal1",
    "DesignLabPage.trustSignal2",
    "DesignLabPage.trustSignal3",
  ],
  insightKey: "DesignLabPage.sampleInsight",
  frameworkTitleKey: "DesignLabPage.sampleFrameworkTitle",
  frameworkTagKey: "DesignLabPage.sampleFrameworkTag",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DesignLabPage() {
  const { t } = useTranslation("pages-core");
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Header */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", py: 4, px: { xs: 2, md: 4 } }}>
        <Container maxWidth="xl">
          <Typography
            variant="overline"
            fontWeight={800}
            sx={{ color: "text.secondary", letterSpacing: "0.1em" }}
          >
            {t("DesignLabPage.internalSurfaceLabel")}
          </Typography>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mt: 0.5 }}>
            {t("DesignLabPage.pageTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 780 }}>
            {t("DesignLabPage.pageIntro")}
          </Typography>
        </Container>
      </Box>

      {/* ====================================================================
          SECTION 1 — Accent color
      ==================================================================== */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <SectionTitle
          label={t("DesignLabPage.section1Label")}
          title={t("DesignLabPage.section1Title")}
          description={t("DesignLabPage.section1Description")}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
            gap: 3,
            mt: 3,
          }}
        >
          {ACCENTS.map((accent) => (
            <AccentSwatch key={accent.key} accent={accent} fontFamily={TYPEFACES[1].family} />
          ))}
        </Box>
      </Container>

      {/* ====================================================================
          SECTION 2 — Typeface
      ==================================================================== */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <SectionTitle
          label={t("DesignLabPage.section2Label")}
          title={t("DesignLabPage.section2Title")}
          description={t("DesignLabPage.section2Description")}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
            mt: 3,
          }}
        >
          {TYPEFACES.map((tf) => (
            <TypefaceSwatch key={tf.key} typeface={tf} accent={ACCENTS[0]} />
          ))}
        </Box>
      </Container>

      {/* ====================================================================
          SECTION 3 — UI detail comparisons (body text, chips, buttons)
      ==================================================================== */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <SectionTitle
          label={t("DesignLabPage.section3Label")}
          title={t("DesignLabPage.section3Title")}
          description={t("DesignLabPage.section3Description")}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
            mt: 3,
          }}
        >
          {TYPEFACES.map((tf) => (
            <UIDetailSwatch key={tf.key} typeface={tf} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Section title helper
// ---------------------------------------------------------------------------

function SectionTitle({
  label,
  title,
  description,
}: {
  label: string;
  title: string;
  description: string;
}) {
  return (
    <Box>
      <Typography variant="caption" fontWeight={500} sx={{ color: "text.secondary", letterSpacing: "0.1em" }}>
        {label.toUpperCase()}
      </Typography>
      <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em", mt: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 820 }}>
        {description}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Accent swatch — full hero/trust/CTA/card sample in a candidate accent
// ---------------------------------------------------------------------------

function AccentSwatch({ accent, fontFamily }: { accent: Accent; fontFamily: string }) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const a = accent.hex;
  return (
    <Paper
      sx={{
        p: 0,
        overflow: "hidden",
        border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
        fontFamily,
      }}
    >
      {/* Header label */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: a,
          color: theme.palette.background.paper,
          fontFamily,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, letterSpacing: "0.1em", fontFamily }}
        >
          {t(accent.labelKey).toUpperCase()} · {accent.hex}
        </Typography>
      </Box>

      {/* Hero */}
      <Box sx={{ p: 3, background: `linear-gradient(135deg, ${alpha(a, 0.08)} 0%, ${alpha(a, 0.02)} 100%)` }}>
        <Chip
          label={t(SAMPLE.eyebrowKey)}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.65rem",
            letterSpacing: "0.08em",
            bgcolor: alpha(a, 0.12),
            color: a,
            fontFamily,
            mb: 1.5,
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            mb: 1,
            fontFamily,
          }}
        >
          {t(SAMPLE.headlineKey)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", lineHeight: 1.6, mb: 2, fontFamily }}
        >
          {t(SAMPLE.subheadKey)}
        </Typography>
        <Stack direction="row" spacing={1.5} mb={2}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AutoAwesomeIcon />}
            sx={{
              bgcolor: a,
              fontWeight: 500,
              textTransform: "none",
              fontFamily,
              "&:hover": { bgcolor: alpha(a, 0.85) },
            }}
          >
            {t(SAMPLE.ctaKey)}
          </Button>
          <Button
            size="small"
            sx={{
              color: a,
              fontWeight: 500,
              textTransform: "none",
              fontFamily,
            }}
          >
            {t(SAMPLE.secondaryKey)}
          </Button>
        </Stack>
        {/* Trust bar */}
        <Stack direction="row" spacing={2} divider={<Box sx={{ width: "1px", bgcolor: alpha(theme.palette.text.primary, 0.15) }} />}>
          {SAMPLE.trustSignalKeys.map((signalKey) => (
            <Typography
              key={signalKey}
              variant="caption"
              sx={{ color: "text.secondary", fontSize: "0.72rem", fontFamily, flex: 1 }}
            >
              {t(signalKey)}
            </Typography>
          ))}
        </Stack>
      </Box>

      {/* Framework card sample */}
      <Box sx={{ p: 2.5, borderTop: 1, borderColor: "divider" }}>
        <Paper
          sx={{
            p: 2,
            border: `1px solid ${alpha(a, 0.2)}`,
            borderLeft: `4px solid ${a}`,
            borderRadius: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="body2" fontWeight={500} sx={{ fontFamily }}>
              {t(SAMPLE.frameworkTitleKey)}
            </Typography>
            <Chip
              label={t(SAMPLE.frameworkTagKey)}
              size="small"
              sx={{
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: alpha(a, 0.1),
                color: a,
                fontFamily,
                height: 18,
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
            <LightbulbIcon sx={{ fontSize: 16, color: a, mt: 0.2 }} />
            <Typography variant="caption" sx={{ lineHeight: 1.6, color: "text.secondary", fontFamily }}>
              {t(SAMPLE.insightKey)}
            </Typography>
          </Stack>
        </Paper>
      </Box>

      {/* Note */}
      <Box sx={{ p: 2.5, bgcolor: alpha(theme.palette.text.primary, 0.02), borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary", fontFamily, lineHeight: 1.6 }}>
          {t(accent.noteKey)}
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Typeface swatch — hero only, but applied to full text content in the font
// ---------------------------------------------------------------------------

function TypefaceSwatch({ typeface, accent }: { typeface: Typeface; accent: Accent }) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const a = accent.hex;
  const ff = typeface.family;
  return (
    <Paper
      sx={{
        p: 0,
        overflow: "hidden",
        border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
      }}
    >
      {/* Header label */}
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: theme.palette.background.paper, color: theme.palette.background.paper }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, letterSpacing: "0.1em", fontFamily: ff }}
        >
          {t(typeface.labelKey).toUpperCase()} · {typeface.family.split(",")[0].replace(/'/g, "")}
        </Typography>
      </Box>

      {/* Hero */}
      <Box sx={{ p: 4, background: `linear-gradient(135deg, ${alpha(a, 0.06)} 0%, ${alpha(a, 0.01)} 100%)`, fontFamily: ff }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: "0.68rem",
            letterSpacing: "0.1em",
            color: a,
            fontFamily: ff,
            textTransform: "uppercase",
          }}
        >
          {t(SAMPLE.eyebrowKey)}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "2.2rem", md: "3rem" },
            fontWeight: 500,
            letterSpacing: "-0.025em",
            lineHeight: 1.1,
            mt: 1.5,
            mb: 2,
            fontFamily: ff,
          }}
        >
          {t(SAMPLE.headlineKey)}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.05rem",
            color: "text.secondary",
            lineHeight: 1.65,
            mb: 3,
            fontFamily: ff,
            fontWeight: 400,
          }}
        >
          {t(SAMPLE.subheadKey)}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          sx={{
            bgcolor: a,
            fontWeight: 500,
            textTransform: "none",
            fontSize: "0.95rem",
            fontFamily: ff,
            px: 2.5,
            py: 1,
            "&:hover": { bgcolor: alpha(a, 0.85) },
          }}
        >
          {t(SAMPLE.ctaKey)}
        </Button>
      </Box>

      {/* Note */}
      <Box sx={{ p: 2.5, bgcolor: alpha(theme.palette.text.primary, 0.02), borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.secondary", fontFamily: ff, lineHeight: 1.6 }}>
          {t(typeface.noteKey)}
        </Typography>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// UI detail swatch — buttons, chips, body, mono in the candidate typeface
// ---------------------------------------------------------------------------

function UIDetailSwatch({ typeface }: { typeface: Typeface }) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const ff = typeface.family;
  const a = ACCENTS[0].hex; // Coral locked
  return (
    <Paper sx={{ p: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, letterSpacing: "0.1em", color: "text.secondary", fontFamily: ff }}
      >
        {t(typeface.labelKey).toUpperCase()}
      </Typography>

      {/* Headings scale */}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontFamily: ff, fontSize: "3rem", fontWeight: 500, letterSpacing: "-0.025em", lineHeight: 1.1 }}>
          {t("DesignLabPage.headingScaleH1")}
        </Typography>
        <Typography sx={{ fontFamily: ff, fontSize: "2rem", fontWeight: 500, letterSpacing: "-0.02em", lineHeight: 1.2, mt: 1 }}>
          {t("DesignLabPage.headingScaleH2")}
        </Typography>
        <Typography sx={{ fontFamily: ff, fontSize: "1.375rem", fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.3, mt: 1 }}>
          {t("DesignLabPage.headingScaleH3")}
        </Typography>
        <Typography sx={{ fontFamily: ff, fontSize: "1rem", fontWeight: 400, lineHeight: 1.6, mt: 2, color: "text.secondary" }}>
          {t("DesignLabPage.bodyTextSample")}
        </Typography>
      </Box>

      {/* Buttons + chips */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Button
            variant="contained"
            sx={{ bgcolor: a, fontFamily: ff, textTransform: "none", fontWeight: 500, "&:hover": { bgcolor: alpha(a, 0.85) } }}
          >
            {t("DesignLabPage.primaryActionButton")}
          </Button>
          <Button
            variant="outlined"
            sx={{ fontFamily: ff, textTransform: "none", fontWeight: 500, borderColor: alpha(theme.palette.text.primary, 0.2), color: "text.primary" }}
          >
            {t("DesignLabPage.secondaryButton")}
          </Button>
          <Button sx={{ fontFamily: ff, textTransform: "none", fontWeight: 500, color: "text.secondary" }}>
            {t("DesignLabPage.textLinkButton")}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={1}>
          <Chip label={t("DesignLabPage.chipFree")} size="small" sx={{ fontFamily: ff, fontWeight: 500, fontSize: "0.62rem", bgcolor: alpha(theme.palette.success.dark, 0.12), color: theme.palette.success.dark }} />
          <Chip label={t("DesignLabPage.chipAllAccess")} size="small" sx={{ fontFamily: ff, fontWeight: 500, fontSize: "0.62rem", bgcolor: alpha(a, 0.12), color: a }} />
          <Chip label={t("DesignLabPage.chipLive")} size="small" sx={{ fontFamily: ff, fontWeight: 500, fontSize: "0.58rem", bgcolor: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.dark }} />
          <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />} label={t("DesignLabPage.chipCritical")} size="small" sx={{ fontFamily: ff, fontWeight: 500, fontSize: "0.6rem", bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }} />
        </Stack>
      </Box>

      {/* Mono */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary", fontFamily: ff }}>
          {t("DesignLabPage.monospaceCaption")}
        </Typography>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 2,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.background.default,
            borderRadius: 1.5,
            fontSize: "0.8rem",
            fontFamily: "'JetBrains Mono', Menlo, monospace",
            overflow: "auto",
          }}
        >
          {t("DesignLabPage.monospaceSample")}
        </Box>
      </Box>
    </Paper>
  );
}
